/* eslint-disable unicorn/filename-case */
import { type useForm } from '@tanstack/react-form';
import { HelpCircle } from '@untitled-ui/icons-react';
import z from 'zod';

import { SimpleTiptapEditor } from '../../../components/editor/simple-tiptap-editor';
import { ErrorField } from '../../../components/inputs/error-field';
import { TextField } from '../../../components/inputs/text-field';
import { SelectField } from '../../../components/select-field';
import { Tooltip } from '../../../components/tooltip';
import { tGlobal, tNamespace } from '../../../services/translation';
import { countriesOptions, getLanguages } from '../../../shared/countries';
import { parseFirstZodError } from '../../../shared/validation';

const t = tNamespace('app.blogs.-modal-rewrite');

function hasTextContent(content: unknown): boolean {
  if (content == null || typeof content !== 'object') {
    return false;
  }

  const node = content as Record<string, unknown>;

  if (node.type === 'text' && typeof node.text === 'string' && node.text.trim().length > 0) {
    return true;
  }

  if (Array.isArray(node.content)) {
    return (node.content as unknown[]).some(function (childNode: unknown) {
      return hasTextContent(childNode);
    });
  }

  return false;
}

type RewriteContentSourceFormProps = {
  form: ReturnType<
    typeof useForm<{
      country: string;
      language: string;
      chosenTitle: string;
      providedContentType: 'url' | 'text';
      url: string;
      tiptapJsonContent: Record<string, any> | null;
      primaryKeyword: string;
      primaryPrompt: string;
      mode: 'light' | 'major';
      contentLength: 'auto' | 'same' | 'custom';
      contentLengthValue: number;
      extraInformation: string;
    }>
  >;
};

export function RewriteContentSourceForm(props: RewriteContentSourceFormProps) {
  const { form } = props;

  return (
    <div className="flex flex-col gap-4">
      <form.Field
        name="chosenTitle"
        validators={{
          onChange: function (data) {
            return parseFirstZodError(z.string().min(3, 'TITLE_REQUIRED'), data.value);
          },
        }}
        // eslint-disable-next-line react/no-children-prop
        children={function (field) {
          return (
            <TextField
              autoFocus
              name={field.name}
              sizeField="md"
              label={<div className="flex items-center gap-1">{t('contentTitle')}</div>}
              placeholder={t('titlePlaceholder')}
              value={field.state.value}
              onChange={function (e) {
                field.handleChange(e.target.value);
              }}
              error={field.state.meta?.errors}
              autoComplete="off"
            />
          );
        }}
      />
      <div className="flex w-full gap-4">
        <div className="w-1/2">
          <form.Field
            name="country"
            validators={{
              onChange: function (data) {
                return parseFirstZodError(z.string().min(1, 'COUNTRY_REQUIRED'), data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <SelectField
                  value={field.state.value}
                  placeholder={tGlobal('selectACountry')}
                  onChange={field.handleChange}
                  options={countriesOptions}
                  size="md"
                  contentFit={true}
                  className="w-full"
                  triggerClassName="w-full"
                  contentClassName="z-[100]"
                  label={tGlobal('country')}
                  tooltip={
                    <Tooltip
                      text={tGlobal('countryTooltip')}
                      description={tGlobal('descCountryTooltip')}
                    >
                      <div>
                        <HelpCircle width={12} height={12} />
                      </div>
                    </Tooltip>
                  }
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </div>
        <div className="w-1/2">
          <form.Field
            name="language"
            validators={{
              onChange: function (data) {
                return parseFirstZodError(z.string().min(1, 'LANGUAGE_REQUIRED'), data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <SelectField
                  value={field.state.value}
                  placeholder={tGlobal('selectALanguage')}
                  onChange={field.handleChange}
                  options={getLanguages()}
                  size="md"
                  contentFit={true}
                  className="w-full"
                  triggerClassName="w-full"
                  contentClassName="z-[100]"
                  label={tGlobal('language')}
                  tooltip={
                    <Tooltip
                      text={tGlobal('languageTooltip')}
                      description={tGlobal('descLanguageTooltip')}
                    >
                      <div>
                        <HelpCircle width={12} height={12} />
                      </div>
                    </Tooltip>
                  }
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </div>
      </div>
      <form.Field
        name="tiptapJsonContent"
        validators={{
          onChange: function (data) {
            const jsonSchema = z
              .object({
                type: z.string(),
                content: z.array(z.any()).optional(),
              })
              .nullable()
              .refine(
                function (val) {
                  return val != null && hasTextContent(val);
                },
                { message: 'CONTENT_REQUIRED' }
              );
            return parseFirstZodError(jsonSchema, data.value);
          },
        }}
        // eslint-disable-next-line react/no-children-prop
        children={function (field) {
          return (
            <div className="w-full">
              <label className="text-gray-700 font-body-2-r mb-2 block">
                {t('pasteContentLabel')}
              </label>
              <div className="rounded-lg border border-branding-accent-200 bg-branding-accent-50 p-4">
                <SimpleTiptapEditor
                  content={field.state.value ?? {}}
                  placeholder={t('contentDescription')}
                  onChange={function (json) {
                    field.handleChange(json);
                  }}
                  className={`w-full ${
                    field.state.meta?.errors != null && field.state.meta.errors.length > 0
                      ? 'border-red-500 has-[:focus]:border-red-500 has-[:focus]:shadow-error'
                      : ''
                  }`}
                  height="200px"
                />
                <p className="mt-2 text-sm text-branding-gray">{t('contentDescription')}</p>
                {field.state.meta?.errors != null && field.state.meta.errors.length > 0 && (
                  <ErrorField error={field.state.meta.errors} sizeField="md" />
                )}
              </div>
            </div>
          );
        }}
      />
      <form.Field
        name="url"
        // eslint-disable-next-line react/no-children-prop
        children={function (field) {
          return (
            <TextField
              name={field.name}
              sizeField="md"
              label={
                <span className="flex gap-1">
                  {t('urlContentLabel')}
                  <span className="font-normal text-branding-gray">{tGlobal('optional')}</span>
                </span>
              }
              placeholder="https://"
              value={field.state.value}
              onChange={function (e) {
                field.handleChange(e.target.value);
              }}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
}
